import FundInvestmentInvestor from '../../../models/admin/FundInvestmentData';
import { currentInvestment } from '../../../models/Investor';
import type { BulkPromoteInvestorItem, BulkPromoteStage } from './investorManagementTypes';

export function requiresInvestmentAmount(
  stage: FundInvestmentInvestor['status'] | 'LEAD_WITH_EOI',
): boolean {
  return stage === 'OPPORTUNITY' || stage === 'COMMITTED_INVESTOR';
}

export function getNextStage(
  currentStage: FundInvestmentInvestor['status'],
): FundInvestmentInvestor['status'] {
  if (!currentStage) {
    throw new Error('Invalid promotion stage: stage is undefined or null');
  }

  switch (currentStage) {
    case 'CANDIDATE':
      return 'LEAD';
    case 'LEAD':
      return 'OPPORTUNITY';
    case 'OPPORTUNITY':
      return 'COMMITTED_INVESTOR';
    case 'COMMITTED_INVESTOR':
      throw new Error('Cannot promote beyond COMMITTED_INVESTOR stage');
    default:
      throw new Error(`Invalid promotion stage: "${currentStage}"`);
  }
}

function parseInvestmentField(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return parseFloat(value) || 0;
  }
  return 0;
}

export function buildBulkPromoteInvestors(
  investors: FundInvestmentInvestor[],
  stage: BulkPromoteStage,
): BulkPromoteInvestorItem[] {
  const eligibleInvestors = investors.filter((investor) => {
    if (stage === 'LEAD_WITH_EOI') {
      return investor.status === 'LEAD' && !!currentInvestment(investor);
    }
    if (stage === 'LEAD') {
      return investor.status === 'LEAD' && !currentInvestment(investor);
    }
    return investor.status === stage;
  });

  return eligibleInvestors.map((investor) => ({
    id: investor.id,
    investor,
    preferredInvestment: parseInvestmentField(investor.preferredInvestment),
    committedInvestment: parseInvestmentField(investor.committedInvestment),
  }));
}

export function getInvestmentPromptPrefill(investor: FundInvestmentInvestor): string {
  if (investor.status === 'LEAD' && investor.eoiSubmitted) {
    return String(investor.indicativeInvestment || investor.preferredInvestment || 0);
  }
  if (investor.status === 'OPPORTUNITY') {
    return String(investor.preferredInvestment || investor.committedInvestment || 0);
  }
  return '';
}

export function updateBulkPromoteInvestorField(
  items: BulkPromoteInvestorItem[] | undefined,
  id: string,
  field: string,
  value: unknown,
): BulkPromoteInvestorItem[] | undefined {
  return items?.map((item) => (item.id === id ? { ...item, [field]: value } : item));
}

export function updateBulkPromoteInvestorAmountField(
  items: BulkPromoteInvestorItem[] | undefined,
  id: string,
  field: 'preferredInvestment' | 'committedInvestment',
  value: string,
): BulkPromoteInvestorItem[] | undefined {
  const numericValue = value === '' ? 0 : parseFloat(value);
  if (isNaN(numericValue)) {
    return items;
  }
  return items?.map((item) => (item.id === id ? { ...item, [field]: numericValue } : item));
}
